import { useEffect } from "react";
import { useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { RocketLaunchIcon } from "@heroicons/react/24/outline";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";

import { useAuth } from "@contexts/AuthContext";
import { useToggle } from "@hooks/hooks";
import { classNames } from "@utils";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";

export const Login = () => {
  const { user, signIn, isLoading } = useAuth();
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
      remember_me: true,
    },
    onSubmit: async ({ value }) => {
      try {
        await signIn(value.email, value.password);
        navigate({ to: "/" });
      } catch (error) {
        const message = error instanceof Error ? error.message : "Login failed";
        toast.custom((t) => (
          <Toast type="error" body={message} t={t} />
        ));
      }
    },
  });

  // Redirect if already logged in
  useEffect(() => {
    if (user && !isLoading) {
      navigate({ to: "/" });
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-200">
          newsbot
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="px-6 py-12 bg-white dark:bg-gray-800 shadow-sm sm:rounded-lg sm:px-12 border border-gray-150 dark:border-gray-775">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="space-y-6"
          >
            <form.Field
              name="email"
              validators={{
                onChange: ({ value }) => (!value ? "Email is required" : undefined),
              }}
            >
              {({ state, handleChange, handleBlur }) => (
                <div>
                  <label
                    htmlFor="email"
                    className="block ml-px text-xs font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    value={state.value}
                    className={classNames(
                      "block mt-1 w-full shadow-xs sm:text-sm rounded-md py-2.5 bg-gray-100 dark:bg-gray-850 dark:text-gray-100",
                      !state.meta.isValid
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 dark:border-gray-700 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500"
                    )}
                  />
                  {state.meta.errors && (
                    <p className="mt-1 text-sm text-red-600">{state.meta.errors[0]}</p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="password"
              validators={{
                onChange: ({ value }) => (!value ? "Password is required" : undefined),
              }}
            >
              {({ state, handleChange, handleBlur }) => (
                <PasswordField
                  value={state.value}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isValid={state.meta.isValid}
                  error={state.meta.errors?.[0]}
                />
              )}
            </form.Field>

            <button
              type="submit"
              className="w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <RocketLaunchIcon className="w-4 h-4 mr-1.5" />
              Sign in
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

interface PasswordFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  isValid: boolean;
  error?: string;
}

function PasswordField({ value, onChange, onBlur, isValid, error }: PasswordFieldProps) {
  const [isVisible, toggleVisibility] = useToggle(false);

  return (
    <div>
      <label
        htmlFor="password"
        className="block ml-px text-xs font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide"
      >
        Password
      </label>
      <div className="relative mt-1">
        <input
          type={isVisible ? "text" : "password"}
          id="password"
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          value={value}
          className={classNames(
            "block w-full shadow-xs sm:text-sm rounded-md py-2.5 bg-gray-100 dark:bg-gray-850 dark:text-gray-100 pr-10",
            !isValid
              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
              : "border-gray-300 dark:border-gray-700 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500"
          )}
        />
        <button
          type="button"
          onClick={toggleVisibility}
          aria-label={isVisible ? "Hide password" : "Show password"}
          className="absolute inset-y-0 right-0 px-3 flex items-center"
        >
          {!isVisible ? (
            <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
          ) : (
            <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
          )}
        </button>
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
